/// Placeholders con la misma silueta que el contenido real, para que la
/// pantalla no salte cuando llegan los datos.

interface BloqueProps {
  className?: string;
  ancho?: string;
}

export function Bloque({ className = '', ancho }: BloqueProps) {
  return (
    <div
      className={`animate-pulse rounded bg-white/[.07] ${className}`}
      style={ancho ? { width: ancho } : undefined}
      aria-hidden
    />
  );
}

/// Tarjeta de evento en la columna del colaborador.
export function EsqueletoTarjetaEvento() {
  return (
    <div className="flex overflow-hidden rounded-tarjeta border border-white/[.09] bg-panel">
      <div className="w-1 flex-none bg-white/10" />
      <div className="flex flex-1 items-center gap-3 px-3 py-3">
        <Bloque className="h-[42px] w-[46px] flex-none rounded-[9px]" />
        <div className="min-w-0 flex-1">
          <Bloque className="h-3.5" ancho="72%" />
          <Bloque className="mt-2 h-2.5" ancho="88%" />
          <Bloque className="mt-2.5 h-4 rounded-full" ancho="96px" />
        </div>
      </div>
    </div>
  );
}

export function EsqueletoListaEventos({ cantidad = 3 }: { cantidad?: number }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="mb-0.5 flex items-baseline justify-between gap-2.5">
        <Bloque className="h-4.5" ancho="150px" />
        <Bloque className="h-3" ancho="110px" />
      </div>
      {Array.from({ length: cantidad }, (_, i) => (
        <EsqueletoTarjetaEvento key={i} />
      ))}
    </div>
  );
}

/// Panel grande de «Lista de confirmados».
export function EsqueletoPanelConfirmados() {
  return (
    <section className="tarjeta overflow-hidden">
      <div className="h-1 bg-white/10" />
      <div className="flex flex-col gap-4 px-5 pt-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:pt-6">
        <div className="min-w-0 flex-1">
          <Bloque className="h-2.5" ancho="130px" />
          <Bloque className="mt-3 h-6" ancho="60%" />
          <Bloque className="mt-2.5 h-3" ancho="80%" />
        </div>
        <Bloque className="h-10 w-[180px] flex-none rounded-[10px]" />
      </div>

      <div className="px-5 pt-5 sm:px-6">
        <Bloque className="h-[58px] w-[180px] rounded-[11px]" />
      </div>

      <div className="mt-5 h-px bg-white/[.08]" />

      <div className="px-5 py-5 sm:px-6 sm:py-6">
        <Bloque className="h-2.5" ancho="90px" />
        <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 rounded-[10px] border border-white/[.06] bg-white/[.04] px-2.5 py-1.5"
            >
              <Bloque className="h-[30px] w-[30px] flex-none rounded-full" />
              <Bloque className="h-3 flex-1" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/// Vista Colaborador completa, antes de que llegue nada.
export function EsqueletoVistaColaborador() {
  return (
    <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-start gap-5 px-4 pb-12 pt-6 sm:px-8 lg:grid-cols-[336px_minmax(0,1fr)]">
      <div className="order-2 min-w-0">
        <EsqueletoPanelConfirmados />
      </div>
      <div className="order-1 min-w-0">
        <EsqueletoListaEventos />
      </div>
    </div>
  );
}

/// Lista lateral de la vista Admin.
export function EsqueletoListaAdmin({ cantidad = 4 }: { cantidad?: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: cantidad }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-2.5 rounded-[10px] bg-white/[.035] px-2.5 py-2.5"
        >
          <Bloque className="w-1 flex-none self-stretch rounded" />
          <div className="min-w-0 flex-1">
            <Bloque className="h-3" ancho="78%" />
            <Bloque className="mt-2 h-2.5" ancho="55%" />
          </div>
        </div>
      ))}
    </div>
  );
}

/// Formulario de evento mientras se carga.
export function EsqueletoFormulario() {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 border-b border-white/[.08] px-5 py-4 sm:px-6">
        <div className="min-w-0 flex-1">
          <Bloque className="h-5" ancho="240px" />
          <Bloque className="mt-2 h-2.5" ancho="170px" />
        </div>
        <Bloque className="h-9 w-[92px] rounded-[9px]" />
        <Bloque className="h-9 w-[92px] rounded-[9px]" />
        <Bloque className="h-9 w-[140px] rounded-[9px]" />
      </div>

      <div className="flex gap-4 border-b border-white/[.08] px-5 pb-3 pt-4 sm:px-6">
        <Bloque className="h-3.5" ancho="70px" />
        <Bloque className="h-3.5" ancho="130px" />
        <Bloque className="h-3.5" ancho="120px" />
      </div>

      <div className="grid max-w-[860px] grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:gap-x-[18px] sm:p-6">
        <div className="sm:col-span-2">
          <Bloque className="h-2.5" ancho="110px" />
          <Bloque className="mt-2 h-11 rounded-[9px]" />
        </div>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i}>
            <Bloque className="h-2.5" ancho="95px" />
            <Bloque className="mt-2 h-11 rounded-[9px]" />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Bloque className="h-2.5" ancho="90px" />
          <Bloque className="mt-2 h-24 rounded-[9px]" />
        </div>
      </div>
    </div>
  );
}

/// Vista Admin completa.
export function EsqueletoVistaAdmin() {
  return (
    <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-start gap-5 px-4 pb-12 pt-6 sm:px-8 lg:grid-cols-[288px_minmax(0,1fr)]">
      <aside className="tarjeta p-4">
        <Bloque className="h-10 rounded-[9px]" />
        <Bloque className="mt-2 h-9 rounded-[9px]" />
        <Bloque className="mb-2.5 mt-5 h-2.5" ancho="90px" />
        <EsqueletoListaAdmin />
      </aside>
      <section className="tarjeta min-h-[520px] overflow-hidden">
        <EsqueletoFormulario />
      </section>
    </div>
  );
}

/// Contenido del modal de un evento mientras se pide el detalle.
export function EsqueletoModalEvento() {
  return (
    <div className="px-5 pb-6 pt-5 sm:px-6">
      <Bloque className="h-6" ancho="55%" />
      <Bloque className="mt-2.5 h-3" ancho="80%" />
      <Bloque className="mt-5 h-3" ancho="100%" />
      <Bloque className="mt-2 h-3" ancho="92%" />

      <Bloque className="mt-6 h-2.5" ancho="120px" />
      <div className="mt-2.5 flex gap-2.5">
        <Bloque className="h-10 w-[104px] rounded-[10px]" />
        <Bloque className="h-10 w-[112px] rounded-[10px]" />
      </div>

      <div className="mt-6 border-t border-dashed border-white/10 pt-4">
        <Bloque className="h-4" ancho="45%" />
        <div className="mt-2.5 flex flex-wrap gap-2.5">
          <Bloque className="h-9 w-[100px] rounded-full" />
          <Bloque className="h-9 w-[120px] rounded-full" />
          <Bloque className="h-9 w-[90px] rounded-full" />
        </div>
      </div>
    </div>
  );
}

/// Pantalla completa de arranque, antes de saber si hay sesión.
export function EsqueletoArranque({ texto }: { texto: string }) {
  return (
    <div className="flex min-h-full flex-col">
      <div className="flex-none">
        <div className="flex h-16 items-center gap-4 bg-panel px-4 sm:px-7">
          <Bloque className="h-[26px] w-[130px]" />
          <div className="flex-1" />
          <Bloque className="h-8 w-8 rounded-full" />
          <Bloque className="hidden h-3 w-[120px] md:block" />
        </div>
        <div className="h-[3px] bg-gradient-to-r from-[#6AB333] via-[#1166B0] to-panel" />
      </div>

      <div className="flex flex-1 items-center justify-center gap-3 px-6 py-24 text-sm text-debil">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-lima" />
        {texto}
      </div>
    </div>
  );
}
